import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  FileText,
  Download,
  AlertCircle,
  CheckCircle,
  Info,
  Euro,
  TrendingUp,
  TrendingDown,
  Calendar,
  Building,
  FileSpreadsheet,
  Loader2,
  Lock,
  Scale,
  BarChart3
} from 'lucide-react';
import { canAccessFeature, getPlanDisplayName } from '../config/planFeatures';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Plan hierarchy helper
const PLAN_LEVELS = { free: 0, premium: 1, governance: 2, pro: 3, enterprise: 4 };

export default function BilanciMinisteriali() {
  const { user } = useAuth();
  const userPlan = user?.plan || 'free';
  const hasGovernanceAccess = canAccessFeature(userPlan, 'governance');
  const hasProAccess = PLAN_LEVELS[userPlan] >= PLAN_LEVELS['pro'];

  const [selectedAnno, setSelectedAnno] = useState(new Date().getFullYear() - 1);
  const [activeTab, setActiveTab] = useState('mod-d');
  const [anteprimaModD, setAnteprimaModD] = useState(null);
  const [anteprimaModA, setAnteprimaModA] = useState(null);
  const [anteprimaModB, setAnteprimaModB] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingPdf, setLoadingPdf] = useState(null);
  const [error, setError] = useState(null);

  const currentYear = new Date().getFullYear();
  const availableYears = Array.from({ length: 5 }, (_, i) => currentYear - i);

  useEffect(() => {
    if (hasGovernanceAccess) {
      fetchAnteprima(activeTab);
    }
  }, [selectedAnno, activeTab, hasGovernanceAccess]);

  const fetchAnteprima = async (tab) => {
    // Check access for pro-only tabs
    if ((tab === 'mod-a' || tab === 'mod-b') && !hasProAccess) {
      return;
    }
    if (!hasGovernanceAccess) return;

    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/bilanci-ministeriali/${tab}/anteprima?anno=${selectedAnno}`, {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        if (tab === 'mod-d') setAnteprimaModD(data);
        else if (tab === 'mod-a') setAnteprimaModA(data);
        else if (tab === 'mod-b') setAnteprimaModB(data);
      } else {
        const errData = await res.json();
        setError(errData.detail || 'Errore nel caricamento dei dati');
      }
    } catch (err) {
      setError('Errore di connessione al server');
    } finally {
      setLoading(false);
    }
  };

  const downloadPdf = async (modello) => {
    setLoadingPdf(modello);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/bilanci-ministeriali/${modello}/pdf?anno=${selectedAnno}`, {
        credentials: 'include'
      });
      
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const filenames = {
          'mod-d': `MOD_D_Rendiconto_Cassa_${selectedAnno}.pdf`,
          'mod-c': `MOD_C_Relazione_Missione_${selectedAnno}.pdf`,
          'mod-a': `MOD_A_Stato_Patrimoniale_${selectedAnno}.pdf`,
          'mod-b': `MOD_B_Rendiconto_Gestionale_${selectedAnno}.pdf`
        };
        a.download = filenames[modello] || `${modello}_${selectedAnno}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } else {
        const errData = await res.json();
        setError(errData.detail || 'Errore nella generazione del PDF');
      }
    } catch (err) {
      setError('Errore durante il download');
    } finally {
      setLoadingPdf(null);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount || 0);
  };

  // Locked view for users without governance access
  if (!hasGovernanceAccess) {
    return (
      <div className="space-y-6" data-testid="bilanci-ministeriali-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Schemi Ministeriali Bilancio</h1>
            <p className="text-slate-600">Generazione bilanci secondo D.M. 5 marzo 2020 n. 39</p>
          </div>
          <Badge variant="secondary" className="bg-amber-100 text-amber-800">
            <Lock className="w-3 h-3 mr-1" />
            Piano Governance+
          </Badge>
        </div>

        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-amber-100 rounded-full">
                <Lock className="w-6 h-6 text-amber-700" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-amber-900 mb-2">Funzionalità Premium</h3>
                <p className="text-amber-800 mb-4">
                  La generazione dei bilanci ministeriali (MOD. A, B, C, D) secondo il D.M. 39/2020 
                  è disponibile dal piano <strong>Governance</strong> in poi.
                </p>
                <p className="text-amber-700 text-sm mb-4">
                  Il tuo piano attuale: <strong>{getPlanDisplayName(userPlan)}</strong>
                </p>
                <Button 
                  onClick={() => window.location.href = '/pricing'}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                >
                  Scopri i Piani
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="bilanci-ministeriali-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Schemi Ministeriali Bilancio</h1>
          <p className="text-slate-600">Generazione bilanci secondo D.M. 5 marzo 2020 n. 39</p>
        </div>
        <div className="flex items-center gap-4">
          <Select value={selectedAnno.toString()} onValueChange={(v) => setSelectedAnno(parseInt(v))}>
            <SelectTrigger className="w-32" data-testid="select-anno">
              <Calendar className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {availableYears.map(year => (
                <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Alert className="border-blue-200 bg-blue-50">
        <Info className="h-4 w-4 text-blue-600" />
        <AlertDescription className="text-blue-800">
          <strong>Normativa:</strong> Gli schemi di bilancio sono conformi al D.M. 5 marzo 2020 n. 39.
          <br />
          <span className="text-sm">
            MOD. D e C: Piano Governance+ | MOD. A e B: Piano Pro+ (ETS con ricavi &gt; €220.000)
          </span>
        </AlertDescription>
      </Alert>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-2 lg:grid-cols-4">
          <TabsTrigger value="mod-d" data-testid="tab-mod-d">
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            MOD. D (Cassa)
          </TabsTrigger>
          <TabsTrigger value="mod-c" data-testid="tab-mod-c">
            <FileText className="w-4 h-4 mr-2" />
            MOD. C (Missione)
          </TabsTrigger>
          <TabsTrigger 
            value="mod-a" 
            data-testid="tab-mod-a"
            disabled={!hasProAccess}
            className={!hasProAccess ? "opacity-50" : ""}
          >
            <Scale className="w-4 h-4 mr-2" />
            MOD. A (Patr.)
            {!hasProAccess && <Lock className="w-3 h-3 ml-1" />}
          </TabsTrigger>
          <TabsTrigger 
            value="mod-b" 
            data-testid="tab-mod-b"
            disabled={!hasProAccess}
            className={!hasProAccess ? "opacity-50" : ""}
          >
            <BarChart3 className="w-4 h-4 mr-2" />
            MOD. B (Gest.)
            {!hasProAccess && <Lock className="w-3 h-3 ml-1" />}
          </TabsTrigger>
        </TabsList>

        {/* TAB MOD. D - Rendiconto per Cassa */}
        <TabsContent value="mod-d" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <FileSpreadsheet className="w-5 h-5 text-blue-600" />
                    MOD. D - Rendiconto per Cassa
                  </CardTitle>
                  <CardDescription className="mt-1">
                    Schema semplificato per ETS con entrate/ricavi inferiori a €220.000 annui.
                  </CardDescription>
                </div>
                <Button 
                  onClick={() => downloadPdf('mod-d')}
                  disabled={loadingPdf === 'mod-d' || loading}
                  data-testid="download-mod-d"
                >
                  {loadingPdf === 'mod-d' ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4 mr-2" />
                  )}
                  Scarica PDF
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                </div>
              ) : anteprimaModD ? (
                <div className="space-y-6">
                  {/* Riepilogo */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="flex items-center gap-2 text-green-700 mb-1">
                        <TrendingUp className="w-4 h-4" />
                        <span className="text-sm font-medium">Totale Entrate</span>
                      </div>
                      <p className="text-2xl font-bold text-green-800" data-testid="totale-entrate">
                        {formatCurrency(anteprimaModD.riepilogo?.totale_entrate)}
                      </p>
                    </div>
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <div className="flex items-center gap-2 text-red-700 mb-1">
                        <TrendingDown className="w-4 h-4" />
                        <span className="text-sm font-medium">Totale Uscite</span>
                      </div>
                      <p className="text-2xl font-bold text-red-800" data-testid="totale-uscite">
                        {formatCurrency(anteprimaModD.riepilogo?.totale_uscite)}
                      </p>
                    </div>
                    <div className={`border rounded-lg p-4 ${
                      anteprimaModD.riepilogo?.avanzo_disavanzo >= 0 
                        ? 'bg-blue-50 border-blue-200' 
                        : 'bg-amber-50 border-amber-200'
                    }`}>
                      <div className={`flex items-center gap-2 mb-1 ${
                        anteprimaModD.riepilogo?.avanzo_disavanzo >= 0 ? 'text-blue-700' : 'text-amber-700'
                      }`}>
                        <Euro className="w-4 h-4" />
                        <span className="text-sm font-medium">Avanzo/Disavanzo</span>
                      </div>
                      <p className={`text-2xl font-bold ${
                        anteprimaModD.riepilogo?.avanzo_disavanzo >= 0 ? 'text-blue-800' : 'text-amber-800'
                      }`} data-testid="avanzo-disavanzo">
                        {formatCurrency(anteprimaModD.riepilogo?.avanzo_disavanzo)}
                      </p>
                    </div>
                  </div>

                  {anteprimaModD.note?.applicabile ? (
                    <Alert className="border-green-200 bg-green-50">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <AlertDescription className="text-green-800">
                        <strong>MOD. D applicabile:</strong> Le entrate dell'anno ({formatCurrency(anteprimaModD.riepilogo?.totale_entrate)}) 
                        sono inferiori alla soglia di €220.000.
                      </AlertDescription>
                    </Alert>
                  ) : (
                    <Alert className="border-amber-200 bg-amber-50">
                      <AlertCircle className="h-4 w-4 text-amber-600" />
                      <AlertDescription className="text-amber-800">
                        <strong>Attenzione:</strong> Le entrate superano €220.000. 
                        È necessario utilizzare i MOD. A e B (Stato Patrimoniale e Rendiconto Gestionale).
                      </AlertDescription>
                    </Alert>
                  )}

                  {/* Tabelle entrate/uscite */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                        <TrendingUp className="w-4 h-4 text-green-600" />
                        Entrate per Sezione
                      </h3>
                      <div className="border rounded-lg overflow-hidden">
                        <table className="w-full text-sm">
                          <thead className="bg-green-600 text-white">
                            <tr>
                              <th className="text-left p-2">Sezione</th>
                              <th className="text-right p-2">Importo</th>
                            </tr>
                          </thead>
                          <tbody>
                            {['A', 'B', 'C', 'D', 'E'].map(key => {
                              const gruppo = anteprimaModD.entrate?.[key];
                              if (!gruppo) return null;
                              return (
                                <tr key={key} className="border-t hover:bg-slate-50">
                                  <td className="p-2 text-xs">
                                    <span className="font-medium">{key})</span> {gruppo.titolo?.substring(0, 30)}...
                                  </td>
                                  <td className="p-2 text-right font-medium">
                                    {formatCurrency(gruppo.totale_gruppo)}
                                  </td>
                                </tr>
                              );
                            })}
                            <tr className="border-t bg-green-50 font-bold">
                              <td className="p-2">TOTALE</td>
                              <td className="p-2 text-right">{formatCurrency(anteprimaModD.entrate?.totale_generale)}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                        <TrendingDown className="w-4 h-4 text-red-600" />
                        Uscite per Sezione
                      </h3>
                      <div className="border rounded-lg overflow-hidden">
                        <table className="w-full text-sm">
                          <thead className="bg-red-600 text-white">
                            <tr>
                              <th className="text-left p-2">Sezione</th>
                              <th className="text-right p-2">Importo</th>
                            </tr>
                          </thead>
                          <tbody>
                            {['A', 'B', 'C', 'D', 'E'].map(key => {
                              const gruppo = anteprimaModD.uscite?.[key];
                              if (!gruppo) return null;
                              return (
                                <tr key={key} className="border-t hover:bg-slate-50">
                                  <td className="p-2 text-xs">
                                    <span className="font-medium">{key})</span> {gruppo.titolo?.substring(0, 30)}...
                                  </td>
                                  <td className="p-2 text-right font-medium">
                                    {formatCurrency(gruppo.totale_gruppo)}
                                  </td>
                                </tr>
                              );
                            })}
                            <tr className="border-t bg-red-50 font-bold">
                              <td className="p-2">TOTALE</td>
                              <td className="p-2 text-right">{formatCurrency(anteprimaModD.uscite?.totale_generale)}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 text-slate-500">
                  <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Nessun dato disponibile per l'anno {selectedAnno}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB MOD. C - Relazione di Missione */}
        <TabsContent value="mod-c" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-purple-600" />
                    MOD. C - Relazione di Missione
                  </CardTitle>
                  <CardDescription className="mt-1">
                    Documento narrativo obbligatorio per tutti gli ETS.
                  </CardDescription>
                </div>
                <Button 
                  onClick={() => downloadPdf('mod-c')}
                  disabled={loadingPdf === 'mod-c'}
                  className="bg-purple-600 hover:bg-purple-700"
                  data-testid="download-mod-c"
                >
                  {loadingPdf === 'mod-c' ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4 mr-2" />
                  )}
                  Scarica Template
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <Alert className="border-purple-200 bg-purple-50 mb-4">
                <Info className="h-4 w-4 text-purple-600" />
                <AlertDescription className="text-purple-800">
                  Il sistema genera un <strong>template precompilato</strong> con i dati statistici 
                  dell'ente che dovrai integrare con le informazioni narrative.
                </AlertDescription>
              </Alert>
              <div className="bg-slate-50 rounded-lg p-6">
                <h4 className="font-semibold text-slate-900 mb-4">Contenuto del Template</h4>
                <ul className="space-y-2 text-sm text-slate-700">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span><strong>Sezione 1:</strong> Informazioni generali sull'ente</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span><strong>Sezione 2:</strong> Attività svolte nell'esercizio</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span><strong>Sezione 3:</strong> Dati statistici (precompilati)</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span><strong>Sezione 4:</strong> Risorse umane impiegate</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span><strong>Sezione 5:</strong> Illustrazione del risultato gestionale</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span><strong>Sezione 6:</strong> Obiettivi e programmi futuri</span>
                  </li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB MOD. A - Stato Patrimoniale */}
        <TabsContent value="mod-a" className="space-y-4">
          {!hasProAccess ? (
            <Card className="border-amber-200 bg-amber-50">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-amber-100 rounded-full">
                    <Lock className="w-6 h-6 text-amber-700" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-amber-900 mb-2">Piano Pro Richiesto</h3>
                    <p className="text-amber-800 mb-2">
                      Il MOD. A (Stato Patrimoniale) è disponibile dal piano <strong>Pro</strong>.
                    </p>
                    <p className="text-amber-700 text-sm">
                      Per ETS con ricavi superiori a €220.000 annui.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Scale className="w-5 h-5 text-indigo-600" />
                      MOD. A - Stato Patrimoniale
                    </CardTitle>
                    <CardDescription className="mt-1">
                      Per ETS con ricavi superiori a €220.000. Attivo e Passivo patrimoniale.
                    </CardDescription>
                  </div>
                  <Button 
                    onClick={() => downloadPdf('mod-a')}
                    disabled={loadingPdf === 'mod-a' || loading}
                    className="bg-indigo-600 hover:bg-indigo-700"
                    data-testid="download-mod-a"
                  >
                    {loadingPdf === 'mod-a' ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4 mr-2" />
                    )}
                    Scarica PDF
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
                  </div>
                ) : anteprimaModA ? (
                  <div className="space-y-6">
                    <Alert className="border-indigo-200 bg-indigo-50">
                      <Info className="h-4 w-4 text-indigo-600" />
                      <AlertDescription className="text-indigo-800">
                        <strong>Nota:</strong> Stato Patrimoniale semplificato derivato dai dati di cassa. 
                        Per un bilancio completo, consultare un commercialista.
                      </AlertDescription>
                    </Alert>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <div className="flex items-center gap-2 text-blue-700 mb-1">
                          <TrendingUp className="w-4 h-4" />
                          <span className="text-sm font-medium">Totale Attivo</span>
                        </div>
                        <p className="text-2xl font-bold text-blue-800">
                          {formatCurrency(anteprimaModA.attivo?.totale_generale)}
                        </p>
                      </div>
                      <div className="bg-rose-50 border border-rose-200 rounded-lg p-4">
                        <div className="flex items-center gap-2 text-rose-700 mb-1">
                          <TrendingDown className="w-4 h-4" />
                          <span className="text-sm font-medium">Totale Passivo</span>
                        </div>
                        <p className="text-2xl font-bold text-rose-800">
                          {formatCurrency(anteprimaModA.passivo?.totale_generale)}
                        </p>
                      </div>
                    </div>

                    {anteprimaModA.quadratura && (
                      <Alert className={anteprimaModA.quadratura.quadra 
                        ? "border-green-200 bg-green-50" 
                        : "border-amber-200 bg-amber-50"}>
                        {anteprimaModA.quadratura.quadra ? (
                          <CheckCircle className="h-4 w-4 text-green-600" />
                        ) : (
                          <AlertCircle className="h-4 w-4 text-amber-600" />
                        )}
                        <AlertDescription className={anteprimaModA.quadratura.quadra 
                          ? "text-green-800" 
                          : "text-amber-800"}>
                          <strong>Quadratura:</strong> Differenza {formatCurrency(anteprimaModA.quadratura.differenza)} - 
                          {anteprimaModA.quadratura.quadra ? " Bilancio quadra!" : " Verificare i dati"}
                        </AlertDescription>
                      </Alert>
                    )}

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      {/* Attivo */}
                      <div>
                        <h3 className="font-semibold text-slate-900 mb-3">ATTIVO</h3>
                        <div className="border rounded-lg overflow-hidden">
                          <table className="w-full text-sm">
                            <thead className="bg-blue-600 text-white">
                              <tr>
                                <th className="text-left p-2">Sezione</th>
                                <th className="text-right p-2">€</th>
                              </tr>
                            </thead>
                            <tbody>
                              {anteprimaModA.attivo?.sezioni && Object.entries(anteprimaModA.attivo.sezioni).map(([key, sez]) => (
                                <tr key={key} className="border-t hover:bg-slate-50">
                                  <td className="p-2 text-xs">{key}) {sez.titolo?.substring(0, 35)}...</td>
                                  <td className="p-2 text-right">{formatCurrency(sez.totale)}</td>
                                </tr>
                              ))}
                              <tr className="border-t bg-blue-50 font-bold">
                                <td className="p-2">TOTALE ATTIVO</td>
                                <td className="p-2 text-right">{formatCurrency(anteprimaModA.attivo?.totale_generale)}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                      {/* Passivo */}
                      <div>
                        <h3 className="font-semibold text-slate-900 mb-3">PASSIVO</h3>
                        <div className="border rounded-lg overflow-hidden">
                          <table className="w-full text-sm">
                            <thead className="bg-rose-600 text-white">
                              <tr>
                                <th className="text-left p-2">Sezione</th>
                                <th className="text-right p-2">€</th>
                              </tr>
                            </thead>
                            <tbody>
                              {anteprimaModA.passivo?.sezioni && Object.entries(anteprimaModA.passivo.sezioni).map(([key, sez]) => (
                                <tr key={key} className="border-t hover:bg-slate-50">
                                  <td className="p-2 text-xs">{key}) {sez.titolo?.substring(0, 35)}...</td>
                                  <td className="p-2 text-right">{formatCurrency(sez.totale)}</td>
                                </tr>
                              ))}
                              <tr className="border-t bg-rose-50 font-bold">
                                <td className="p-2">TOTALE PASSIVO</td>
                                <td className="p-2 text-right">{formatCurrency(anteprimaModA.passivo?.totale_generale)}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12 text-slate-500">
                    <Scale className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>Clicca su questo tab per caricare l'anteprima</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* TAB MOD. B - Rendiconto Gestionale */}
        <TabsContent value="mod-b" className="space-y-4">
          {!hasProAccess ? (
            <Card className="border-amber-200 bg-amber-50">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-amber-100 rounded-full">
                    <Lock className="w-6 h-6 text-amber-700" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-amber-900 mb-2">Piano Pro Richiesto</h3>
                    <p className="text-amber-800 mb-2">
                      Il MOD. B (Rendiconto Gestionale) è disponibile dal piano <strong>Pro</strong>.
                    </p>
                    <p className="text-amber-700 text-sm">
                      Per ETS con ricavi superiori a €220.000 annui.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <BarChart3 className="w-5 h-5 text-teal-600" />
                      MOD. B - Rendiconto Gestionale
                    </CardTitle>
                    <CardDescription className="mt-1">
                      Per ETS con ricavi superiori a €220.000. Oneri e Proventi per competenza.
                    </CardDescription>
                  </div>
                  <Button 
                    onClick={() => downloadPdf('mod-b')}
                    disabled={loadingPdf === 'mod-b' || loading}
                    className="bg-teal-600 hover:bg-teal-700"
                    data-testid="download-mod-b"
                  >
                    {loadingPdf === 'mod-b' ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4 mr-2" />
                    )}
                    Scarica PDF
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
                  </div>
                ) : anteprimaModB ? (
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <div className="flex items-center gap-2 text-green-700 mb-1">
                          <TrendingUp className="w-4 h-4" />
                          <span className="text-sm font-medium">Totale Proventi</span>
                        </div>
                        <p className="text-2xl font-bold text-green-800">
                          {formatCurrency(anteprimaModB.proventi?.totale_generale)}
                        </p>
                      </div>
                      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                        <div className="flex items-center gap-2 text-red-700 mb-1">
                          <TrendingDown className="w-4 h-4" />
                          <span className="text-sm font-medium">Totale Oneri</span>
                        </div>
                        <p className="text-2xl font-bold text-red-800">
                          {formatCurrency(anteprimaModB.oneri?.totale_generale)}
                        </p>
                      </div>
                      <div className={`border rounded-lg p-4 ${
                        anteprimaModB.risultato?.avanzo_disavanzo >= 0 
                          ? 'bg-teal-50 border-teal-200' 
                          : 'bg-amber-50 border-amber-200'
                      }`}>
                        <div className={`flex items-center gap-2 mb-1 ${
                          anteprimaModB.risultato?.avanzo_disavanzo >= 0 ? 'text-teal-700' : 'text-amber-700'
                        }`}>
                          <Euro className="w-4 h-4" />
                          <span className="text-sm font-medium">Risultato</span>
                        </div>
                        <p className={`text-2xl font-bold ${
                          anteprimaModB.risultato?.avanzo_disavanzo >= 0 ? 'text-teal-800' : 'text-amber-800'
                        }`}>
                          {formatCurrency(anteprimaModB.risultato?.avanzo_disavanzo)}
                        </p>
                        <p className="text-xs text-slate-600 mt-1">
                          {anteprimaModB.risultato?.tipo === 'avanzo' ? 'Avanzo di gestione' : 'Disavanzo di gestione'}
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      {/* Proventi */}
                      <div>
                        <h3 className="font-semibold text-slate-900 mb-3">PROVENTI E RICAVI</h3>
                        <div className="border rounded-lg overflow-hidden">
                          <table className="w-full text-sm">
                            <thead className="bg-green-600 text-white">
                              <tr>
                                <th className="text-left p-2">Sezione</th>
                                <th className="text-right p-2">€</th>
                              </tr>
                            </thead>
                            <tbody>
                              {anteprimaModB.proventi?.sezioni && Object.entries(anteprimaModB.proventi.sezioni).map(([key, sez]) => (
                                <tr key={key} className="border-t hover:bg-slate-50">
                                  <td className="p-2 text-xs">{key}) {sez.titolo?.substring(0, 40)}...</td>
                                  <td className="p-2 text-right">{formatCurrency(sez.totale)}</td>
                                </tr>
                              ))}
                              <tr className="border-t bg-green-50 font-bold">
                                <td className="p-2">TOTALE PROVENTI</td>
                                <td className="p-2 text-right">{formatCurrency(anteprimaModB.proventi?.totale_generale)}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                      {/* Oneri */}
                      <div>
                        <h3 className="font-semibold text-slate-900 mb-3">ONERI E COSTI</h3>
                        <div className="border rounded-lg overflow-hidden">
                          <table className="w-full text-sm">
                            <thead className="bg-red-600 text-white">
                              <tr>
                                <th className="text-left p-2">Sezione</th>
                                <th className="text-right p-2">€</th>
                              </tr>
                            </thead>
                            <tbody>
                              {anteprimaModB.oneri?.sezioni && Object.entries(anteprimaModB.oneri.sezioni).map(([key, sez]) => (
                                <tr key={key} className="border-t hover:bg-slate-50">
                                  <td className="p-2 text-xs">{key}) {sez.titolo?.substring(0, 40)}...</td>
                                  <td className="p-2 text-right">{formatCurrency(sez.totale)}</td>
                                </tr>
                              ))}
                              <tr className="border-t bg-red-50 font-bold">
                                <td className="p-2">TOTALE ONERI</td>
                                <td className="p-2 text-right">{formatCurrency(anteprimaModB.oneri?.totale_generale)}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12 text-slate-500">
                    <BarChart3 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>Clicca su questo tab per caricare l'anteprima</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Reference info */}
      <Card className="bg-slate-50">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Building className="w-5 h-5 text-slate-400 mt-0.5" />
            <div className="text-sm text-slate-600">
              <p className="font-medium text-slate-700 mb-1">Riferimenti Normativi</p>
              <p>
                D.M. 5 marzo 2020, n. 39 (G.U. n. 102 del 18/04/2020) - 
                "Adozione della modulistica di bilancio degli enti del Terzo settore"
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
